import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client.js'
import type { AiProvider, SettingsResponse } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { Notice, Spinner } from '../components/ui.js'
import { PageHead } from './Layout.js'
import { OfficeAccess } from './OfficeAccess.js'

/** What each function sends, shown next to the switch so nobody has to guess. */
const SENT: { key: 'photoCheck' | 'importAssist'; title: string; sends: string; never: string }[] = [
  {
    key: 'photoCheck',
    title: 'KI-Fotoprüfung',
    sends: 'Vorher- und Nachher-Foto einer erledigten Aufgabe, verkleinert, und der Name der Leistung (z. B. „Treppenhausreinigung“).',
    never: 'Keine Namen von Mitarbeitenden oder Bewohnern, keine Adresse, kein Kunde, kein Datum.',
  },
  {
    key: 'importAssist',
    title: 'KI-Import-Assistent',
    sends: 'Die Spaltenüberschriften einer Importdatei und drei Beispielzeilen. Spalten mit Namen, Telefon oder E-Mail werden vorher ersetzt.',
    never: 'Nicht die Datei selbst, keine personenbezogenen Werte.',
  },
]

/**
 * Einstellungen: at the moment the switches that decide what, if anything,
 * leaves Hauswerk for an AI model. Every change is logged with who and when.
 */
export function SettingsPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: () => api<SettingsResponse>('/api/settings') })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/settings', { method: 'PATCH', body }),
    onSuccess: async () => {
      toast('Gespeichert.')
      await queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })

  if (isLoading || !data) return <Spinner />
  const { settings, providers } = data
  const active = providers.find((p) => p.provider === settings.ai.provider)
  const aiOff = settings.ai.provider === 'off'

  return (
    <>
      <PageHead title="Einstellungen" subtitle="Zugänge, Standort, Qualität und KI: was geprüft wird und was dafür das Haus verlässt." />

      <OfficeAccess />

      <section className="card" style={{ marginBottom: 18 }}>
        <div className="card-head">
          <h2>
            <Icon name="pin" size={16} /> Standort beim Abhaken
          </h2>
          <span className={`badge ${settings.gps.enabled ? 'done' : ''}`}>{settings.gps.enabled ? 'an' : 'aus'}</span>
        </div>
        <div className="card-body stack">
          <label className="switch-row">
            <input type="checkbox" checked={settings.gps.enabled} onChange={(e) => save.mutate({ gps: { enabled: e.target.checked } })} />
            <span>
              <span className="strong">Standort als Nachweis anbieten</span>
              <span className="muted small">
                Beim Abhaken einer Aufgabe schickt die App einmal den Standort mit, und nur bei Mitarbeitenden, die in ihrer App
                ausdrücklich zugestimmt haben. Keine Verfolgung über den Tag, keine Wege, keine Karte der Mitarbeitenden. Im Nachweis
                steht der Punkt nur bei der einzelnen Aufgabe.
              </span>
            </span>
          </label>
          <Notice kind="info">
            Bitte vor dem Einschalten mit den Mitarbeitenden besprechen und, falls es einen Betriebsrat gibt, mit ihm abstimmen.
            Einschalten, Zustimmen und Widerrufen werden protokolliert.
          </Notice>
        </div>
      </section>

      <section className="card" style={{ marginBottom: 18 }}>
        <div className="card-head">
          <h2>
            <Icon name="star" size={16} /> Qualitätsbegehung
          </h2>
        </div>
        <div className="card-body">
          <label className="field" style={{ maxWidth: 360 }}>
            <span>Bestanden ab (Prozent)</span>
            <input
              type="number"
              min={50}
              max={100}
              step={5}
              defaultValue={settings.qualityThreshold}
              onBlur={(e) => {
                const value = Number(e.target.value)
                if (value >= 50 && value <= 100 && value !== settings.qualityThreshold) save.mutate({ qualityThreshold: value })
              }}
            />
            <span className="hint">
              Eine Begehung gilt als bestanden, wenn sie mindestens diesen Anteil der möglichen Punkte erreicht. Übliche Werte liegen
              zwischen 75 und 85.
            </span>
          </label>
        </div>
      </section>

      <section className="card" style={{ marginBottom: 18 }}>
        <div className="card-head">
          <h2>
            <Icon name="check" size={16} /> Fotoprüfung ohne KI
          </h2>
        </div>
        <div className="card-body">
          <label className="switch-row">
            <input
              type="checkbox"
              checked={settings.localPhotoCheck}
              onChange={(e) => save.mutate({ localPhotoCheck: e.target.checked })}
            />
            <span>
              <span className="strong">Fotos auf unserem Server prüfen</span>
              <span className="muted small">
                Erkennt sehr dunkle Fotos, dasselbe Foto bei mehreren Aufgaben, identische Vorher- und Nachher-Bilder und Fotos, die lange
                vor der Erledigung aufgenommen wurden. Es wird nichts nach außen gesendet.
              </span>
            </span>
          </label>
        </div>
      </section>

      <section className="card" style={{ marginBottom: 18 }}>
        <div className="card-head">
          <h2>
            <Icon name="sparkle" size={16} /> KI
          </h2>
          <span className={`badge ${aiOff ? '' : active?.ready ? 'done' : 'warn'}`}>
            {aiOff ? 'aus' : active?.ready ? 'bereit' : 'nicht eingerichtet'}
          </span>
        </div>
        <div className="card-body stack">
          <div>
            <div className="strong" style={{ marginBottom: 8 }}>
              Wo die KI läuft
            </div>
            <div className="provider-list">
              {providers.map((provider) => (
                <label key={provider.provider} className={`provider ${settings.ai.provider === provider.provider ? 'on' : ''}`}>
                  <input
                    type="radio"
                    name="provider"
                    checked={settings.ai.provider === provider.provider}
                    onChange={() => save.mutate({ ai: { provider: provider.provider as AiProvider } })}
                  />
                  <span>
                    <span className="strong">{provider.label}</span>
                    {provider.provider !== 'off' && (
                      <span className={`badge ${provider.ready ? 'done' : ''}`} style={{ marginLeft: 8 }}>
                        {provider.ready ? 'bereit' : 'nicht eingerichtet'}
                      </span>
                    )}
                    <span className="muted small">{provider.location}</span>
                    <span className="muted small">{provider.detail}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {SENT.map((fn) => (
            <label key={fn.key} className={`switch-row ${aiOff ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                disabled={aiOff}
                checked={settings.ai[fn.key]}
                onChange={(e) => save.mutate({ ai: { [fn.key]: e.target.checked } })}
              />
              <span>
                <span className="strong">{fn.title}</span>
                <span className="small">
                  <span className="muted">Wird gesendet: </span>
                  {fn.sends}
                </span>
                <span className="small">
                  <span className="muted">Nie: </span>
                  {fn.never}
                </span>
              </span>
            </label>
          ))}

          {settings.ai.provider === 'anthropic' && (
            <Notice kind="warn">Anthropic direkt ist für Tests mit Beispieldaten gedacht. Für echte Daten den Betrieb in Frankfurt oder auf eigenem Server wählen.</Notice>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <h2>
            <Icon name="lock" size={16} /> Grundsätze
          </h2>
        </div>
        <div className="card-body">
          <ul className="principles">
            <li>Die KI bewertet Ergebnisse an Objekten, nie Personen. Es gibt keine Rangliste und keine automatische Folge für einzelne Mitarbeitende.</li>
            <li>Jedes KI-Ergebnis ist als Vorschlag gekennzeichnet. Ein Mensch bestätigt oder verwirft, beides wird protokolliert.</li>
            <li>Gesendet wird nur, was die jeweilige Frage braucht. Keine Namen, keine Adressen, keine Verläufe.</li>
            <li>Für den Betrieb: Verarbeitung in der EU ohne Speicherung und ohne Training, oder auf unserem eigenen Server.</li>
            <li>Jede Funktion ist einzeln abschaltbar. Ohne KI arbeitet Hauswerk vollständig weiter.</li>
          </ul>
        </div>
      </section>
    </>
  )
}
